import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const DoorTypeSelector = ({
  initialSelection = {
    doorType: "center",
    doorFinish: "stainless",
  },
  onSelectionChange,
  onContinue,
  onBack,
}) => {
  const [selection, setSelection] = useState(initialSelection);

  // Add window size tracking for responsive design
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isSmallMobile = windowWidth <= 480;

  // Update parent component when door selection changes
  useEffect(() => {
    if (onSelectionChange) {
      // Format the selection data for compatibility with config summary
      const doorTypeOptions = {
        center: "Center Opening Door",
        "side-left": "Side Opening - Left Door",
        "side-right": "Side Opening - Right Door",
        channel: "Channel Gate Door",
        glass: "Full Glass Door",
        folding: "Folding Door",
      };

      const doorFinishOptions = {
        stainless: "Stainless Steel Finish",
        rosegold: "Rose Gold Finish",
        gold: "Gold Finish",
      };

      // Create an array with formatted options for config summary
      const formattedSelection = [
        `door_type_${selection.doorType}`,
        `door_finish_${selection.doorFinish}`,
      ];

      onSelectionChange(formattedSelection);
    }
  }, [selection, onSelectionChange]);

  useEffect(() => {
    if (initialSelection) {
      setSelection(initialSelection);
    }
    
  }, []);

  const handleDoorTypeChange = (type) => {
    setSelection((prev) => ({
      ...prev,
      doorType: type,
    }));
  };

  const handleFinishChange = (finish) => {
    setSelection((prev) => ({
      ...prev,
      doorFinish: finish,
    }));
  };

  // Get responsive styles based on screen width
  const getResponsiveStyles = () => {
    return {
      container: {
        padding: isSmallMobile ? "15px 10px" : isMobile ? "20px 15px" : "20px",
      },
      header: {
        marginBottom: isSmallMobile ? "20px" : "30px",
      },
      title: {
        fontSize: isSmallMobile ? "22px" : isMobile ? "24px" : "28px",
        marginBottom: isSmallMobile ? "4px" : "6px",
      },
      subtitle: {
        fontSize: isSmallMobile ? "14px" : "16px",
      },
      mainContent: {
        padding: isSmallMobile ? "15px" : isMobile ? "20px" : "30px",
      },
      section: {
        marginBottom: isSmallMobile ? "25px" : "40px",
      },
      sectionTitle: {
        fontSize: isSmallMobile ? "18px" : isMobile ? "19px" : "20px",
        marginBottom: isSmallMobile ? "6px" : "8px",
      },
      sectionDescription: {
        fontSize: isSmallMobile ? "14px" : "15px",
        marginBottom: isSmallMobile ? "18px" : "25px",
      },
      doorTypesGrid: {
        gridTemplateColumns: isMobile
          ? "1fr"
          : isTablet
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(300px, 1fr))",
        gap: isSmallMobile ? "15px" : "25px",
      },
      doorTypeCard: {
        flexDirection: isSmallMobile ? "column" : "column",
      },
      doorVisual: {
        height: isSmallMobile ? "120px" : isMobile ? "140px" : "160px",
        padding: isSmallMobile ? "20px 15px" : "30px 20px",
      },
      doorFrame: {
        width: isSmallMobile ? "100px" : "120px",
        height: isSmallMobile ? "110px" : isMobile ? "120px" : "140px",
      },
      doorInfo: {
        padding: isSmallMobile ? "15px" : "20px",
      },
      doorTitle: {
        fontSize: isSmallMobile ? "16px" : "18px",
        marginBottom: isSmallMobile ? "8px" : "10px",
      },
      doorDescription: {
        fontSize: isSmallMobile ? "13px" : "14px",
        marginBottom: isSmallMobile ? "10px" : "15px",
      },
      finishesGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(240px, 1fr))",
        gap: isSmallMobile ? "15px" : "20px",
      },
      finishCard: {
        padding: isSmallMobile ? "12px" : "15px",
      },
      finishSwatch: {
        width: isSmallMobile ? "45px" : isMobile ? "50px" : "60px",
        height: isSmallMobile ? "45px" : isMobile ? "50px" : "60px",
        marginRight: isSmallMobile ? "12px" : "15px",
      },
      finishName: {
        fontSize: isSmallMobile ? "15px" : "16px",
      },
      finishDescription: {
        fontSize: isSmallMobile ? "12px" : "13px",
      },
      noteCard: {
        padding: isSmallMobile ? "15px" : "20px",
        flexDirection: isSmallMobile ? "column" : "row",
      },
      noteIcon: {
        marginRight: isSmallMobile ? "0" : "15px",
        marginBottom: isSmallMobile ? "10px" : "0",
        fontSize: isSmallMobile ? "20px" : "24px",
        alignItems: isSmallMobile ? "center" : "flex-start",
      },
      summaryPanel: {
        padding: isSmallMobile ? "15px" : "20px",
        marginTop: isSmallMobile ? "20px" : "30px",
        marginBottom: isSmallMobile ? "20px" : "30px",
      },
      summaryGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(180px, 1fr))",
      },
      navigation: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "12px" : "0",
      },
      backButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "10px 15px" : "12px 24px",
      },
      nextButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "10px 15px" : "12px 24px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{ ...styles.header, ...responsiveStyles.header }}>
          <h2 style={{ ...styles.title, ...responsiveStyles.title }}>
            <i className="fas fa-door-open" style={styles.titleIcon}></i>
            Door Configuration
          </h2>
          <p style={{ ...styles.subtitle, ...responsiveStyles.subtitle }}>
            Select the door type and finish for your elevator
          </p>
        </div>

        <div style={{ ...styles.mainContent, ...responsiveStyles.mainContent }}>
          {/* Door Type Section */}
          <section style={{ ...styles.section, ...responsiveStyles.section }}>
            <h3
              style={{
                ...styles.sectionTitle,
                ...responsiveStyles.sectionTitle,
              }}
            >
              Door Type
            </h3>
            <p
              style={{
                ...styles.sectionDescription,
                ...responsiveStyles.sectionDescription,
              }}
            >
              Choose the opening mechanism that best suits your requirements
            </p>

            <div
              style={{
                ...styles.doorTypesGrid,
                ...responsiveStyles.doorTypesGrid,
              }}
            >
              {/* Center Opening Door */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "center"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("center")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.centerDoor}>
                      <div style={styles.doorLeft}></div>
                      <div style={styles.doorRight}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Center Opening
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Doors open from the center, providing a balanced and elegant
                    entry. Most common choice for passenger elevators.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Faster opening and closing</li>
                      <li>Symmetrical appearance</li>
                      <li>Standard in modern buildings</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "center" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Side Opening - Left */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "side-left"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("side-left")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.sideDoor}>
                      <div style={styles.doorSideLeft}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Side Opening - Left
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Door slides open from the left side. Ideal for installations
                    with space constraints on one side.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Space-efficient on right side</li>
                      <li>Optimal for corner installations</li>
                      <li>Suitable for service corridors</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "side-left" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Side Opening - Right */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "side-right"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("side-right")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.sideDoor}>
                      <div style={styles.doorSideRight}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Side Opening - Right
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Door slides open from the right side. Perfect for specific
                    architectural requirements.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Space-efficient on left side</li>
                      <li>Ideal for specific traffic flow patterns</li>
                      <li>Works well in tight spaces</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "side-right" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Additional Door Types (conditionally shown on larger screens or separate section on mobile) */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "channel"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("channel")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.channelDoor}>
                      <div style={styles.channelPanel}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Channel Gate
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Accordion-style gate that retracts to the side. Classic
                    option for vintage buildings and freight elevators.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Nostalgic, classic appearance</li>
                      <li>Manual or automatic operation</li>
                      <li>Ideal for historic renovations</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "channel" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Full Glass Door */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "glass"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("glass")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.glassDoor}>
                      <div style={styles.glassPanel}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Full Glass Door
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Elegant transparent or frosted glass doors that maximize
                    visibility and create an open feel.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Modern, luxurious appearance</li>
                      <li>Creates sense of openness</li>
                      <li>Perfect for panoramic elevators</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "glass" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Folding Door */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.02, y: -3 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.doorTypeCard,
                  ...responsiveStyles.doorTypeCard,
                  ...(selection.doorType === "folding"
                    ? styles.selectedCard
                    : {}),
                }}
                onClick={() => handleDoorTypeChange("folding")}
              >
                <div
                  style={{
                    ...styles.doorVisual,
                    ...responsiveStyles.doorVisual,
                  }}
                >
                  <div
                    style={{
                      ...styles.doorFrame,
                      ...responsiveStyles.doorFrame,
                    }}
                  >
                    <div style={styles.foldingDoor}>
                      <div style={styles.doorFold1}></div>
                      <div style={styles.doorFold2}></div>
                    </div>
                  </div>
                </div>
                <div
                  style={{ ...styles.doorInfo, ...responsiveStyles.doorInfo }}
                >
                  <h4
                    style={{
                      ...styles.doorTitle,
                      ...responsiveStyles.doorTitle,
                    }}
                  >
                    Folding
                  </h4>
                  <p
                    style={{
                      ...styles.doorDescription,
                      ...responsiveStyles.doorDescription,
                    }}
                  >
                    Multi-panel door that folds to the side. Often used in
                    retrofits and historic buildings.
                  </p>
                  {!isSmallMobile && (
                    <ul style={styles.benefitsList}>
                      <li>Great for limited space</li>
                      <li>Unique aesthetic appeal</li>
                      <li>Suitable for renovations</li>
                    </ul>
                  )}
                </div>
                {selection.doorType === "folding" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>
            </div>
          </section>

          {/* Door Finish Section */}
          <section style={{ ...styles.section, ...responsiveStyles.section }}>
            <h3
              style={{
                ...styles.sectionTitle,
                ...responsiveStyles.sectionTitle,
              }}
            >
              Door Finish
            </h3>
            <p
              style={{
                ...styles.sectionDescription,
                ...responsiveStyles.sectionDescription,
              }}
            >
              Select the material and finish for your elevator doors
            </p>

            <div
              style={{
                ...styles.finishesGrid,
                ...responsiveStyles.finishesGrid,
              }}
            >
              {/* Stainless Steel Finish */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.03 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.finishCard,
                  ...responsiveStyles.finishCard,
                  ...(selection.doorFinish === "stainless"
                    ? styles.selectedFinish
                    : {}),
                }}
                onClick={() => handleFinishChange("stainless")}
              >
                <div
                  style={{
                    ...styles.finishSwatch,
                    ...responsiveStyles.finishSwatch,
                    ...styles.stainlessSwatch,
                  }}
                ></div>
                <div style={styles.finishInfo}>
                  <h4
                    style={{
                      ...styles.finishName,
                      ...responsiveStyles.finishName,
                    }}
                  >
                    Stainless Steel
                  </h4>
                  <p
                    style={{
                      ...styles.finishDescription,
                      ...responsiveStyles.finishDescription,
                    }}
                  >
                    Classic, modern look with brushed or mirror finish. Durable
                    and easy to clean.
                  </p>
                </div>
                {selection.doorFinish === "stainless" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Rose Gold Finish */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.03 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.finishCard,
                  ...responsiveStyles.finishCard,
                  ...(selection.doorFinish === "rosegold"
                    ? styles.selectedFinish
                    : {}),
                }}
                onClick={() => handleFinishChange("rosegold")}
              >
                <div
                  style={{
                    ...styles.finishSwatch,
                    ...responsiveStyles.finishSwatch,
                    ...styles.roseGoldSwatch,
                  }}
                ></div>
                <div style={styles.finishInfo}>
                  <h4
                    style={{
                      ...styles.finishName,
                      ...responsiveStyles.finishName,
                    }}
                  >
                    Rose Gold
                  </h4>
                  <p
                    style={{
                      ...styles.finishDescription,
                      ...responsiveStyles.finishDescription,
                    }}
                  >
                    Sophisticated warm tone with pink undertones. Elegant and
                    contemporary.
                  </p>
                </div>
                {selection.doorFinish === "rosegold" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>

              {/* Gold Finish */}
              <motion.div
                whileHover={!isMobile ? { scale: 1.03 } : {}}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.finishCard,
                  ...responsiveStyles.finishCard,
                  ...(selection.doorFinish === "gold"
                    ? styles.selectedFinish
                    : {}),
                }}
                onClick={() => handleFinishChange("gold")}
              >
                <div
                  style={{
                    ...styles.finishSwatch,
                    ...responsiveStyles.finishSwatch,
                    ...styles.goldSwatch,
                  }}
                ></div>
                <div style={styles.finishInfo}>
                  <h4
                    style={{
                      ...styles.finishName,
                      ...responsiveStyles.finishName,
                    }}
                  >
                    Gold
                  </h4>
                  <p
                    style={{
                      ...styles.finishDescription,
                      ...responsiveStyles.finishDescription,
                    }}
                  >
                    Luxurious golden finish for upscale buildings and premium
                    aesthetics.
                  </p>
                </div>
                {selection.doorFinish === "gold" && (
                  <div style={styles.selectedIndicator}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </motion.div>
            </div>
          </section>

          {/* Educational Note */}
          <div style={{ ...styles.noteCard, ...responsiveStyles.noteCard }}>
            <div style={{ ...styles.noteIcon, ...responsiveStyles.noteIcon }}>
              <i className="fas fa-lightbulb"></i>
            </div>
            <div style={styles.noteContent}>
              <h4 style={styles.noteTitle}>Door Selection Guide</h4>
              <p style={styles.noteText}>
                Door type impacts both aesthetics and functionality. Center
                opening doors are most common in passenger elevators, while side
                opening doors may be more suitable for service elevators or
                narrower shafts. Consider your building's traffic flow,
                available space, and design preferences when making your
                selection.
              </p>
            </div>
          </div>
        </div>

        {/* Selection Summary */}
        <div
          style={{ ...styles.summaryPanel, ...responsiveStyles.summaryPanel }}
        >
          <h4 style={styles.summaryTitle}>Your Door Selection</h4>
          <div
            style={{ ...styles.summaryGrid, ...responsiveStyles.summaryGrid }}
          >
            <div style={styles.summaryItem}>
              <div style={styles.summaryLabel}>Door Type</div>
              <div style={styles.summaryValue}>
                {selection.doorType === "center"
                  ? "Center Opening"
                  : selection.doorType === "side-left"
                  ? "Side Opening - Left"
                  : selection.doorType === "side-right"
                  ? "Side Opening - Right"
                  : selection.doorType === "channel"
                  ? "Channel Gate"
                  : selection.doorType === "glass"
                  ? "Full Glass Door"
                  : selection.doorType === "folding"
                  ? "Folding"
                  : "Standard"}
              </div>
            </div>

            <div style={styles.summaryItem}>
              <div style={styles.summaryLabel}>Door Finish</div>
              <div style={styles.summaryValue}>
                {selection.doorFinish === "stainless"
                  ? "Stainless Steel"
                  : selection.doorFinish === "rosegold"
                  ? "Rose Gold"
                  : selection.doorFinish === "gold"
                  ? "Gold"
                  : "Standard"}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div style={{ ...styles.navigation, ...responsiveStyles.navigation }}>
          <motion.button
            whileHover={
              !isSmallMobile ? { scale: 1.03, backgroundColor: "#f5f5f7" } : {}
            }
            whileTap={{ scale: 0.98 }}
            onClick={onBack}
            style={{ ...styles.backButton, ...responsiveStyles.backButton }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </motion.button>

          <motion.button
            whileHover={
              !isSmallMobile
                ? { scale: 1.03, boxShadow: "0 8px 20px rgba(0,122,255,0.3)" }
                : {}
            }
            whileTap={{ scale: 0.98 }}
            onClick={onContinue}
            style={{ ...styles.nextButton, ...responsiveStyles.nextButton }}
          >
            Continue <i className="fas fa-arrow-right"></i>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    margin: "0 0 6px",
    color: "#1d1d1f",
  },
  titleIcon: {
    marginRight: "8px",
    color: "#007aff",
  },
  subtitle: {
    fontSize: "16px",
    color: "#4d4d4d",
    margin: 0,
  },
  mainContent: {
    backgroundColor: "#fff",
    borderRadius: "15px",
    padding: "30px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.06)",
  },
  section: {
    marginBottom: "40px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: "0 0 8px",
    color: "#1d1d1f",
  },
  sectionDescription: {
    fontSize: "15px",
    color: "#666",
    marginBottom: "25px",
  },
  doorTypesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "25px",
    marginBottom: "20px",
  },
  doorTypeCard: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    overflow: "hidden",
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    cursor: "pointer",
    position: "relative",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      borderColor: "#b0d5ff",
    },
  },
  selectedCard: {
    borderColor: "#007aff",
    boxShadow: "0 5px 20px rgba(0,122,255,0.15)",
    backgroundColor: "#f0f7ff",
  },
  doorVisual: {
    padding: "30px 20px",
    backgroundColor: "#f9f9fb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "160px",
  },
  doorFrame: {
    width: "120px",
    height: "140px",
    backgroundColor: "#e8e8e8",
    borderRadius: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "5px",
  },
  centerDoor: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f0f0f0",
    position: "relative",
    overflow: "hidden",
  },
  doorLeft: {
    position: "absolute",
    left: "0",
    top: "0",
    width: "50%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(-10px)",
  },
  doorRight: {
    position: "absolute",
    right: "0",
    top: "0",
    width: "50%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(10px)",
  },
  sideDoor: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f0f0f0",
    position: "relative",
    overflow: "hidden",
  },
  doorSide: {
    position: "absolute",
    right: "0",
    top: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(60%)",
  },
  foldingDoor: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f0f0f0",
    display: "flex",
    overflow: "hidden",
  },
  doorFold1: {
    width: "50%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(-5px)",
  },
  doorFold2: {
    width: "50%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(5px)",
  },
  doorInfo: {
    padding: "20px",
    flex: 1,
  },
  doorTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginTop: "0",
    marginBottom: "10px",
    color: "#333",
  },
  doorDescription: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5",
    marginBottom: "15px",
  },
  benefitsList: {
    fontSize: "14px",
    color: "#555",
    paddingLeft: "20px",
    margin: "0",
    "& > li": {
      marginBottom: "5px",
    },
  },
  selectedIndicator: {
    position: "absolute",
    top: "15px",
    right: "15px",
    fontSize: "22px",
    color: "#007aff",
  },
  finishesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "20px",
  },
  finishCard: {
    display: "flex",
    alignItems: "center",
    padding: "15px",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative",
    backgroundColor: "#fff",
    "&:hover": {
      borderColor: "#b0d5ff",
      boxShadow: "0 4px 12px rgba(0,122,255,0.1)",
    },
  },
  selectedFinish: {
    borderColor: "#007aff",
    backgroundColor: "#f0f7ff",
    boxShadow: "0 4px 12px rgba(0,122,255,0.1)",
  },
  finishSwatch: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    marginRight: "15px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  stainlessSwatch: {
    background: "linear-gradient(135deg, #b8b8b8, #e8e8e8, #d4d4d4)",
  },
  paintedSwatch: {
    background: "linear-gradient(135deg, #4a90e2, #5da0f2)",
  },
  glassSwatch: {
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.7), rgba(200,230,255,0.7))",
    border: "1px solid #e0e0e0",
    position: "relative",
    "&:after": {
      content: '""',
      position: "absolute",
      top: "0",
      left: "20%",
      right: "20%",
      bottom: "0",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.5), rgba(255,255,255,0.1), rgba(255,255,255,0.5))",
    },
  },
  bronzeSwatch: {
    background: "linear-gradient(135deg, #b08d57, #d4b483)",
  },
  finishInfo: {
    flex: 1,
  },
  finishName: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 5px",
    color: "#333",
  },
  finishDescription: {
    fontSize: "13px",
    color: "#666",
    margin: "0",
    lineHeight: "1.4",
  },
  noteCard: {
    display: "flex",
    borderRadius: "12px",
    backgroundColor: "#fffbf0",
    padding: "20px",
    marginTop: "10px",
    border: "1px solid #f7e9bc",
  },
  noteIcon: {
    fontSize: "24px",
    color: "#f5a623",
    marginRight: "15px",
    display: "flex",
    alignItems: "flex-start",
    padding: "3px",
  },
  noteContent: {
    flex: 1,
  },
  noteTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 8px",
    color: "#333",
  },
  noteText: {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.5",
    margin: "0",
  },
  summaryPanel: {
    backgroundColor: "#f9f9fb",
    borderRadius: "12px",
    padding: "20px",
    marginTop: "30px",
    marginBottom: "30px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #eaeaec",
  },
  summaryTitle: {
    margin: "0 0 15px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "15px",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  summaryLabel: {
    fontSize: "13px",
    color: "#666",
  },
  summaryValue: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#333",
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
  },
  backButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #d0d0d0",
    backgroundColor: "white",
    color: "#4d4d4d",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f7",
    },
  },
  nextButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#007aff",
    color: "white",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 12px rgba(0,122,255,0.2)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#0062cc",
    },
  },
  // Add to the existing styles object
  doorSideLeft: {
    position: "absolute",
    left: "0",
    top: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(-60%)",
  },
  doorSideRight: {
    position: "absolute",
    right: "0",
    top: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "#007aff",
    transition: "transform 0.3s ease",
    transform: "translateX(60%)",
  },
  channelDoor: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f0f0f0",
    position: "relative",
    overflow: "hidden",
  },
  channelPanel: {
    position: "absolute",
    left: "0",
    top: "0",
    width: "100%",
    height: "100%",
    backgroundImage:
      "repeating-linear-gradient(90deg, #007aff, #007aff 10px, #4d9fff 10px, #4d9fff 20px)",
    transition: "transform 0.3s ease",
    transform: "translateX(-70%)",
  },
  glassDoor: {
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(240, 240, 240, 0.3)",
    position: "relative",
    overflow: "hidden",
    border: "1px solid #ccc",
  },
  glassPanel: {
    position: "absolute",
    left: "0",
    top: "0",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.5), rgba(200,230,255,0.3))",
    transition: "transform 0.3s ease",
    transform: "translateX(-60%)",
    borderRight: "1px solid rgba(255,255,255,0.8)",
  },
  roseGoldSwatch: {
    background: "linear-gradient(135deg, #e0bfb8, #e7ccbe, #dba99a)",
  },
  goldSwatch: {
    background: "linear-gradient(135deg, #d4af37, #f9d776, #d4af37)",
  },
  // Mobile responsiveness
  "@media (max-width: 768px)": {
    mainContent: {
      padding: "20px",
    },
    doorTypesGrid: {
      gridTemplateColumns: "1fr",
      gap: "20px",
    },
    finishesGrid: {
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    },
    summaryGrid: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  "@media (max-width: 480px)": {
    mainContent: {
      padding: "15px",
    },
    doorVisual: {
      height: "130px",
    },
    finishesGrid: {
      gridTemplateColumns: "1fr",
    },
    finishCard: {
      padding: "12px",
    },
    finishSwatch: {
      width: "50px",
      height: "50px",
    },
    summaryGrid: {
      gridTemplateColumns: "1fr",
    },
    navigation: {
      flexDirection: "column",
      gap: "15px",
    },
    backButton: {
      width: "100%",
      justifyContent: "center",
    },
    nextButton: {
      width: "100%",
      justifyContent: "center",
    },
  },
};

export default DoorTypeSelector;
